import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quickstart-for-adobe-document-generation-api-python"
    }}>{`Quickstart for Adobe Document Generation API (Python)`}</h1>
    <p>{`To get started using Adobe Document Generation API, let's walk through a simple scenario - using a Word document as a template for dynamic receipt generation in PDF. In this guide, we will walk you through the complete process for creating a program that will accomplish this task.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To complete this guide, you will need:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.python.org/downloads/"
        }}>{`Python`}</a>{` - Python 3.10 or higher is required.`}</li>
      <li parentName="ul">{`An Adobe ID. If you do not have one, the credential setup will walk you through creating one.`}</li>
      <li parentName="ul">{`A way to edit code. No specific editor is required for this guide.`}</li>
    </ul>
    <h2 {...{
      "id": "step-one-getting-credentials"
    }}>{`Step One: Getting credentials`}</h2>
    <p>{`1) To begin, open your browser to `}<a parentName="p" {...{
        "href": "https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api"
      }}>{`https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api`}</a>{`. If you are not already logged in to Adobe.com, you will need to sign in or create a new user. Using a personal email account is recommend and not a federated ID.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "750px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/5530d/shot1.webp 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0c8fb/shot1.webp 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/3ea8a/shot1.webp 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/dd4a7/shot1.png 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0f09e/shot1.png 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png",
            "alt": "Sign in",
            "title": "Sign in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) After registering or logging in, you will then be asked to name your new credentials. Use the name, "New Project".`}</p>
    <p>{`3) Change the "Choose language" setting to "Python".`}</p>
    <p>{`4) Also note the checkbox by, "Create personalized code sample." This will include a large set of samples along with your credentials. These can be helpful for learning more later.`}</p>
    <p>{`5) Click the checkbox saying you agree to the developer terms and then click "Create credentials."`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "54.0625%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/5530d/shot2_spc.webp 320w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/0c8fb/shot2_spc.webp 640w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/94b1e/shot2_spc.webp 1280w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/0b34d/shot2_spc.webp 1920w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/0f226/shot2_spc.webp 2450w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/dd4a7/shot2_spc.png 320w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/0f09e/shot2_spc.png 640w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/bbbf7/shot2_spc.png 1280w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/ac7a9/shot2_spc.png 1920w", "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/35cde/shot2_spc.png 2450w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/df40c2e1b4927580098557dcc29eb61b/bbbf7/shot2_spc.png",
            "alt": "Project setup",
            "title": "Project setup",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`6) After your credentials are created, they are automatically downloaded:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/5530d/shot3_spc.webp 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0c8fb/shot3_spc.webp 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/94b1e/shot3_spc.webp 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/c5b4b/shot3_spc.webp 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/dd4a7/shot3_spc.png 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0f09e/shot3_spc.png 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/d866c/shot3_spc.png 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png",
            "alt": "Credentials",
            "title": "Credentials",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-two-setting-up-the-project"
    }}>{`Step Two: Setting up the project`}</h2>
    <p>{`1) In your Downloads folder, find the ZIP file with your credentials: PDFServicesSDK-PythonSamples.zip. If you unzip that archive, you will find a folder of samples and the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "740px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "64.99999999999999%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/5530d/shot5_spc.webp 320w", "/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/0c8fb/shot5_spc.webp 640w", "/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/c30c1/shot5_spc.webp 740w"],
            "sizes": "(max-width: 740px) 100vw, 740px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/dd4a7/shot5_spc.png 320w", "/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/0f09e/shot5_spc.png 640w", "/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/b38af/shot5_spc.png 740w"],
            "sizes": "(max-width: 740px) 100vw, 740px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/c6f5584f126d995bb07a77f5889143b2/b38af/shot5_spc.png",
            "alt": "Samples",
            "title": "Samples",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Take the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file and place it in a new directory. Remember that these credential files are important and should be stored safely.`}</p>
    <p>{`3) At the command line, change to the directory you created, and run the following command to install the Python SDK: `}<inlineCode parentName="p">{`pip install pdfservices-sdk`}</inlineCode>{`.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "33.125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/5530d/shot7.webp 320w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/0c8fb/shot7.webp 640w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/94b1e/shot7.webp 1280w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/0b34d/shot7.webp 1920w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/d5269/shot7.webp 2560w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/e4c85/shot7.webp 2742w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/dd4a7/shot7.png 320w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/0f09e/shot7.png 640w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/bbbf7/shot7.png 1280w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/ac7a9/shot7.png 1920w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/c7a69/shot7.png 2560w", "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/9fbb6/shot7.png 2742w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/f9f94b459b0f3416e44a87797ea368f8/bbbf7/shot7.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`At this point, we've installed the Python SDK for Adobe PDF Services API as a dependency for our project and have copied over our credentials files.`}</p>
    <p>{`Our application will take a Word document, `}<inlineCode parentName="p">{`receiptTemplate.docx`}</inlineCode>{` (downloadable from `}<a parentName="p" {...{
        "href": "/document-services/docs/receiptTemplate.docx"
      }}>{`here`}</a>{`), and combine it with data in a JSON file, `}<inlineCode parentName="p">{`receipt.json`}</inlineCode>{` (downloadable from `}<a parentName="p" {...{
        "href": "/document-services/docs/receipt.json"
      }}>{`here`}</a>{`), to be sent to the Acrobat Services API and generate a receipt PDF.`}</p>
    <p>{`4) In your editor, open the directory where you previously copied the credentials. Create a new file, `}<inlineCode parentName="p">{`generate_pdf.py`}</inlineCode>{`.`}</p>
    <p>{`Now you're ready to begin coding.`}</p>
    <h2 {...{
      "id": "step-three-creating-the-application"
    }}>{`Step Three: Creating the application`}</h2>
    <p>{`1) Let's start by looking at the Word template. If you open the document in Microsoft Word, you'll notice multiple tokens throughout the document (called out by the use of `}<inlineCode parentName="p">{`{{`}</inlineCode>{` and `}<inlineCode parentName="p">{`}}`}</inlineCode>{`).`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "109.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/98518ce1f0921da4983ede2890a013de/5530d/shot10.webp 320w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/0c8fb/shot10.webp 640w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/94b1e/shot10.webp 1280w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/708fc/shot10.webp 1359w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/98518ce1f0921da4983ede2890a013de/dd4a7/shot10.png 320w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/0f09e/shot10.png 640w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/bbbf7/shot10.png 1280w", "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/14c08/shot10.png 1359w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/98518ce1f0921da4983ede2890a013de/bbbf7/shot10.png",
            "alt": "Example of tokens",
            "title": "Example of tokens",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Next, let's look at our sample data:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "author": "Gary Lee",
  "Company": {
    "Name": "Projected",
    "Address": "19718 Mandrake Way",
    "PhoneNumber": "+1-100000098"
  },
  "Invoice": {
    "Date": "January 15, 2021",
    "Number": 123,
    "Items": [
      {
        "item": "Gloves",
        "description": "Microwave gloves",
        "UnitPrice": 5,
        "Quantity": 2,
        "Total": 10
      },
      {
        "item": "Bowls",
        "description": "Microwave bowls",
        "UnitPrice": 10,
        "Quantity": 2,
        "Total": 20
      }
    ]
  },
  "Customer": {
    "Name": "Collins Candy",
    "Address": "315 Dunning Way",
    "PhoneNumber": "+1-200000046",
    "Email": "cc@abcdef.co.dw"
  },
  "Tax": 5,
  "Shipping": 5,
  "clause": {
    "overseas": "The shipment might take 5-10 more than informed."
  },
  "paymentMethod": "Cash"
}
`}</code></pre>
    <p>{`Notice how the tokens in the Word document match up with values in our JSON. While our example will use a hard coded set of data in a file, production applications can get their data from anywhere. Now let's get into our code.`}</p>
    <p>{`3) We'll begin by including our required dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import logging
import os
from datetime import datetime

from adobe.pdfservices.operation.auth.service_principal_credentials import ServicePrincipalCredentials
from adobe.pdfservices.operation.exception.exceptions import ServiceApiException, ServiceUsageException, SdkException
from adobe.pdfservices.operation.io.cloud_asset import CloudAsset
from adobe.pdfservices.operation.io.stream_asset import StreamAsset
from adobe.pdfservices.operation.pdf_services import PDFServices
from adobe.pdfservices.operation.pdf_services_media_type import PDFServicesMediaType
from adobe.pdfservices.operation.pdfjobs.jobs.document_merge_job import DocumentMergeJob
from adobe.pdfservices.operation.pdfjobs.params.documentmerge.document_merge_params import DocumentMergeParams
from adobe.pdfservices.operation.pdfjobs.params.documentmerge.output_format import OutputFormat
from adobe.pdfservices.operation.pdfjobs.result.document_merge_result import DocumentMergePDFResult
`}</code></pre>
    <p>{`4) Set the environment variables `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_SECRET`}</inlineCode>{` by running the following commands and replacing placeholders `}<inlineCode parentName="p">{`YOUR CLIENT ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`YOUR CLIENT SECRET`}</inlineCode>{` with the credentials present in `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Windows:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`MacOS/Linux:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p>{`5) Next, we can create our credentials and use them:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# Initial setup, create credentials instance
credentials = ServicePrincipalCredentials(
    client_id=os.getenv('PDF_SERVICES_CLIENT_ID'),
    client_secret=os.getenv('PDF_SERVICES_CLIENT_SECRET'))

# Creates a PDF Services instance
pdf_services = PDFServices(credentials=credentials)
`}</code></pre>
    <p>{`6) Now, let's create an assets from source file and upload.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`file = open('./receiptTemplate.docx', 'rb')
input_stream = file.read()
file.close()

# Creates an asset(s) from source file(s) and upload
input_asset = pdf_services.upload(input_stream=input_stream,
                                  mime_type=PDFServicesMediaType.PDF)
`}</code></pre>
    <p>{`7) Now, let's create the parameters and the job:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# Setup input data for the document merge process
with open('./receipt.json', 'r') as file:
    content_string = file.read()
json_data_for_merge = json.loads(content_string)

# Create parameters for the job
document_merge_params = DocumentMergeParams(json_data_for_merge=json_data_for_merge,
                                            output_format=OutputFormat.PDF)
                            
# Creates a new job instance
document_merge_job = DocumentMergeJob(input_asset=input_asset,
                                      document_merge_params=document_merge_params)                                                                                        
`}</code></pre>
    <p>{`This set of code defines what we're doing (a document merge operation, the SDK's way of describing Document Generation), points to our local JSON file and specifies the output is a PDF. It also points to the Word file used as a template.`}</p>
    <p>{`8) The next code block submits the job and gets the job result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# Submit the job and gets the job result
location = pdf_services.submit(document_merge_job)
pdf_services_response = pdf_services.get_job_result(location, DocumentMergePDFResult)

# Get content from the resulting asset(s)
result_asset: CloudAsset = pdf_services_response.get_result().get_asset()
stream_asset: StreamAsset = pdf_services.get_content(result_asset)
`}</code></pre>
    <p>{`9) The next code block saves the result at the specified location:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`output_file_path = 'output/MergeDocumentToPDF.pdf'
with open(output_file_path, "wb") as file:
    file.write(stream_asset.get_input_stream())
`}</code></pre>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "20.625%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/5530d/shot8.webp 320w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/0c8fb/shot8.webp 640w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/94b1e/shot8.webp 1280w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/4b981/shot8.webp 1716w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/dd4a7/shot8.png 320w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/0f09e/shot8.png 640w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/bbbf7/shot8.png 1280w", "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/010a2/shot8.png 1716w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/6176f262376e8e706a019f82c3a72fd6/bbbf7/shot8.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Here's the complete application (`}<inlineCode parentName="p">{`src/documentmerge/merge_document_to_pdf.py`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import json
import logging
import os
from datetime import datetime

from adobe.pdfservices.operation.auth.service_principal_credentials import ServicePrincipalCredentials
from adobe.pdfservices.operation.exception.exceptions import ServiceApiException, ServiceUsageException, SdkException
from adobe.pdfservices.operation.io.cloud_asset import CloudAsset
from adobe.pdfservices.operation.io.stream_asset import StreamAsset
from adobe.pdfservices.operation.pdf_services import PDFServices
from adobe.pdfservices.operation.pdf_services_media_type import PDFServicesMediaType
from adobe.pdfservices.operation.pdfjobs.jobs.document_merge_job import DocumentMergeJob
from adobe.pdfservices.operation.pdfjobs.params.documentmerge.document_merge_params import DocumentMergeParams
from adobe.pdfservices.operation.pdfjobs.params.documentmerge.output_format import OutputFormat
from adobe.pdfservices.operation.pdfjobs.result.document_merge_result import DocumentMergePDFResult

# Initialize the logger
logging.basicConfig(level=logging.INFO)

# This sample illustrates how to merge the Word based document template with the input JSON data to generate
# the output document in the PDF format.
#
# Refer to README.md for instructions on how to run the samples.

class MergeDocumentToPDF:
    def __init__(self):
        try:
            file = open('./receiptTemplate.docx', 'rb')
            input_stream = file.read()
            file.close()

            # Initial setup, create credentials instance
            credentials = ServicePrincipalCredentials(
                client_id=os.getenv('PDF_SERVICES_CLIENT_ID'),
                client_secret=os.getenv('PDF_SERVICES_CLIENT_SECRET')
            )

            # Creates a PDF Services instance
            pdf_services = PDFServices(credentials=credentials)

            # Creates an asset(s) from source file(s) and upload
            input_asset = pdf_services.upload(input_stream=input_stream,
                                              mime_type=PDFServicesMediaType.DOCX)

            # Setup input data for the document merge process
            with open('./receipt.json', 'r') as file:
                content_string = file.read()
            json_data_for_merge = json.loads(content_string)

            # Create parameters for the job
            document_merge_params = DocumentMergeParams(json_data_for_merge=json_data_for_merge,
                                                        output_format=OutputFormat.PDF)

            # Creates a new job instance
            document_merge_job = DocumentMergeJob(input_asset=input_asset,
                                                  document_merge_params=document_merge_params)

            # Submit the job and gets the job result
            location = pdf_services.submit(document_merge_job)
            pdf_services_response = pdf_services.get_job_result(location, DocumentMergePDFResult)

            # Get content from the resulting asset(s)
            result_asset: CloudAsset = pdf_services_response.get_result().get_asset()
            stream_asset: StreamAsset = pdf_services.get_content(result_asset)

            # Creates an output stream and copy stream asset's content to it
            output_file_path = 'output/MergeDocumentToPDF.pdf'
            with open(output_file_path, "wb") as file:
                file.write(stream_asset.get_input_stream())

        except (ServiceApiException, ServiceUsageException, SdkException) as e:
            logging.exception(f'Exception encountered while executing operation: {e}')

if __name__ == "__main__":
    MergeDocumentToPDF()
`}</code></pre>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`Now that you've successfully performed your first operation, `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/document-services/docs/overview/pdf-services-api/"
      }}>{`review the documentation`}</a>{` for many other examples and reach out on our `}<a parentName="p" {...{
        "href": "https://community.adobe.com/t5/acrobat-services-api/ct-p/ct-Document-Cloud-SDK?page=1&sort=latest_replies&filter=all&lang=all&tabid=discussions&topics=label-accessibilityauto-tagapi"
      }}>{`forums`}</a>{` with any questions. Also remember the samples you downloaded while creating your credentials also have many demos.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      